import { createInMemoryStorageHandler, StorageHandler } from "@/lib/storage";
import { makeAutoObservable } from "mobx";

export interface UserState {
  dismissedTourGuideIds?: string[];
  finishedTourGuideIds?: string[];
  dismissedChecklistIds?: string[];
  updatedAt?: number;
  isHydrated?: boolean;
}

export class UserStateStorage {
  private isPreview: boolean;
  private storage: StorageHandler;
  private _state: UserState;

  constructor(isPreview: boolean, storage: StorageHandler) {
    this.isPreview = isPreview;
    this.storage = storage;
    this._state = this.get();
    makeAutoObservable(this);
  }

  private storageKey(): string {
    return `pyne-user-state${this.isPreview ? "preview" : ""}`;
  }

  get dismissedTourGuideIds(): string[] {
    return this._state.dismissedTourGuideIds || [];
  }

  get finishedTourGuideIds(): string[] {
    return this._state.finishedTourGuideIds || [];
  }

  get dismissedChecklistIds(): string[] {
    return this._state.dismissedChecklistIds || [];
  }

  get isHydrated(): boolean {
    return this._state.isHydrated || false;
  }

  public isExists(): boolean {
    return this.storage.isKeyExists(this.storageKey());
  }

  public get(): UserState {
    return this.storage.get(this.storageKey(), {});
  }

  public clear(): void {
    this.storage.remove(this.storageKey());
    this._state = {};
  }

  public set(state: Partial<UserState>): void {
    this._state = { ...this._state, ...state, updatedAt: Date.now() };
    this.storage.set(this.storageKey(), this._state);
  }

  public addFinishedGuide(guideId: string): void {
    const finishedTourGuideIds = this._state.finishedTourGuideIds || [];
    this.set({ finishedTourGuideIds: [...finishedTourGuideIds, guideId] });
  }

  public addDismissedGuide(guideId: string): void {
    const dismissedTourGuideIds = this._state.dismissedTourGuideIds || [];
    this.set({ dismissedTourGuideIds: [...dismissedTourGuideIds, guideId] });
  }

  public addDismissedChecklist(checklistId: string): void {
    const dismissedChecklistIds = this._state.dismissedChecklistIds || [];
    this.set({ dismissedChecklistIds: [...dismissedChecklistIds, checklistId] });
  }

  public hydrate(state: Partial<UserState>): void {
    const currentState = this._state;

    if (Object.keys(state).length === 0) {
      const hydratedState = { ...currentState, isHydrated: true };
      this._state = hydratedState;
      this.storage.set(this.storageKey(), hydratedState);
      return;
    }

    const isUserStateStale = state.updatedAt && currentState.updatedAt && state.updatedAt > currentState.updatedAt;
    if (isUserStateStale) {
      return;
    }

    const hydratedState = { ...state, isHydrated: true };
    this._state = hydratedState;
    this.storage.set(this.storageKey(), hydratedState);
  }
}

export const buildInMemoryUserStateStorage = () => new UserStateStorage(false, createInMemoryStorageHandler());
